let instance = null;

class EventModule {
    constructor() {
        if (instance) return instance;
        instance = this;

        this.eventHandlers = {};
        this.targetEventHandlers = {};
        this.pollInterval = 3000;
        this.isPolling = false;
        this.retryTimeout = null;
        this.maxRetryDelay = 10000;
        this.currentRetryDelay = 1000;
        this.currentTargetId = null;

        this.startPolling();
        window.addEventListener('beforeunload', () => this.stopPolling());
    }

    startPolling() {
        if (this.isPolling) return;
        this.isPolling = true;
        this.poll();
    }

    stopPolling() {
        this.isPolling = false;
        if (this.retryTimeout) {
            clearTimeout(this.retryTimeout);
            this.retryTimeout = null;
        }
    }

    setTargetId(targetId) {
        this.currentTargetId = targetId;
    }

    clearTargetId() {
        this.currentTargetId = null;
    }

    async poll() {
        if (!this.isPolling) return;

        try {
            const url = this.currentTargetId 
                ? `/api/events/?targetId=${encodeURIComponent(this.currentTargetId)}`
                : '/api/events/';

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                credentials: 'same-origin'
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const data = await response.json();
            
            if (data.status === 'success') {
                this.currentRetryDelay = 1000;
                
                if (data.events?.length > 0) {
                    data.events.forEach(event => this.handleEvent(event));
                }
            }

            this.retryTimeout = setTimeout(() => this.poll(), this.pollInterval);

        } catch (error) {
            console.error('Events polling error:', error);
            this.currentRetryDelay = Math.min(this.currentRetryDelay * 2, this.maxRetryDelay);
            this.retryTimeout = setTimeout(() => this.poll(), this.currentRetryDelay);
        }
    }

    handleEvent(event) {
        if (this.eventHandlers[event.type]) {
            this.eventHandlers[event.type].forEach(handler => {
                try {
                    handler(event.data);
                } catch (error) {
                    console.error(`Event handler error [${event.type}]:`, error);
                }
            });
        }

        if (event.targetId && this.targetEventHandlers[event.type]) {
            const targetHandlers = this.targetEventHandlers[event.type][event.targetId];
            if (targetHandlers) {
                targetHandlers.forEach(handler => {
                    try {
                        handler(event.data);
                    } catch (error) {
                        console.error(`Target event handler error [${event.type}:${event.targetId}]:`, error);
                    }
                });
            }
        }
    }

    on(eventType, handler) {
        if (!this.eventHandlers[eventType]) {
            this.eventHandlers[eventType] = [];
        }
        this.eventHandlers[eventType].push(handler);
    }

    off(eventType, handler) {
        if (!this.eventHandlers[eventType]) return;
        this.eventHandlers[eventType] = this.eventHandlers[eventType]
            .filter(h => h !== handler);
    }

    onTarget(eventType, targetId, handler) {
        if (!this.targetEventHandlers[eventType]) {
            this.targetEventHandlers[eventType] = {};
        }
        
        if (!this.targetEventHandlers[eventType][targetId]) {
            this.targetEventHandlers[eventType][targetId] = [];
        }
        
        this.targetEventHandlers[eventType][targetId].push(handler);
        
        if (!this.currentTargetId) {
            this.setTargetId(targetId);
        }
    }

    offTarget(eventType, targetId, handler) {
        if (!this.targetEventHandlers[eventType] || 
            !this.targetEventHandlers[eventType][targetId]) return;
        
        if (handler) {
            this.targetEventHandlers[eventType][targetId] = 
                this.targetEventHandlers[eventType][targetId].filter(h => h !== handler);
            
            if (this.targetEventHandlers[eventType][targetId].length === 0) {
                delete this.targetEventHandlers[eventType][targetId];
            }
        } else {
            delete this.targetEventHandlers[eventType][targetId];
        }
        
        if (this.currentTargetId === targetId && 
            (!this.targetEventHandlers[eventType] || 
             !this.targetEventHandlers[eventType][targetId])) {
            this.clearTargetId();
        }
    }
}

export default () => new EventModule();
